import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import MenuIcon from '@mui/icons-material/Menu';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Link as RouterLink } from 'react-router-dom';

const leftPages = [
  { title: 'HOME', path: '/' },
  { title: 'THIS IS US', path: '/team' },
  { title: 'ABOUT US', path: '/about' },
];

const rightPages = [
  { title: 'PRICES', path: '/prices' },
  { title: 'REVIEWS', path: '/reviews' },
  { title: 'CONTACT', path: '/contact' },
];

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar position="sticky" color="primary" sx={{ backgroundColor: '#a88685' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{ color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {[...leftPages, ...rightPages].map((page) => (
                <MenuItem 
                  key={page.title} 
                  onClick={handleCloseNavMenu}
                  component={RouterLink}
                  to={page.path}
                >
                  <Typography textAlign="center">{page.title}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Mobile Logo */}
          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontWeight: 700,
              color: 'primary.main',
              textDecoration: 'none',
            }}
          >

                <img
                  src="/images/content/logo.jpg"
                  alt="Stylist Inside Logo"
                  style={{
                    height: 'auto',
                    width: '150px',
                    margin: '8px 0',
                  }}
                />
          </Typography>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, width: '100%', justifyContent: 'center', position: 'relative' }}>
            {/* Centered group: left links, logo, right links */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: 'auto', mx: 'auto' }}>
              {/* Left side menu */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {leftPages.map((page) => (
                  <Button
                    key={page.title}
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    sx={{ 
                      my: 2, 
                      color: 'white', 
                      display: 'block',
                      '&:hover': {
                        color: 'secondary.main',
                      }
                    }}
                  >
                    {page.title}
                  </Button>
                ))}
              </Box>

              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                <img
                  src="/images/content/logo.jpg"
                  alt="Stylist Inside Logo"
                  style={{
                    height: 'auto',
                    width: '150px',
                    margin: '8px 0',
                  }}
                />
              </Box>

              {/* Right side menu */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {rightPages.map((page) => (
                  <Button
                    key={page.title}
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    sx={{ 
                      my: 2, 
                      color: 'white', 
                      display: 'block',
                      '&:hover': {
                        color: 'secondary.main',
                      }
                    }}
                  >
                    {page.title}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Fixed vertical stack for ONLINE STORE and CALL FOR APPOINTMENT */}
            <Box sx={{ position: 'fixed', top: '10mm', right: '32px', zIndex: 1300, display: 'flex', flexDirection: 'column', gap: 2 }}>
              
              <Button
                variant="outlined"
                color="secondary"
                href="tel:8135916538"
                sx={{
                  color: 'white',
                  borderRadius: '24px',
                  border: '2px solid white',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1,
                  minWidth: '140px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  boxShadow: 3,
                  background: 'rgba(168,134,133,0.95)',
                }}
              >
                <PhoneIcon sx={{ mr: 1 }} />
                <span>(813) 591-6538</span>
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                href="https://app.salonrunner.com/customer/directory/index.htm?id=35742"
                target="_blank"
                sx={{ 
                  color: 'white',
                  borderRadius: '24px',
                  border: '2px solid white',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1,
                  minWidth: '100px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  boxShadow: 3,
                  background: 'rgba(168,134,133,0.95)',
                }}
              >
                SCHEDULE APPOINTMENT
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                href="https://shop.saloninteractive.com/store/StylistInside?utm_source=SalonInteractive&utm_medium=web&utm_campaign=ShareMyStore"
                target="_blank"
                sx={{ 
                  color: 'white',
                  borderRadius: '24px',
                  border: '2px solid white',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1,
                  minWidth: '100px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  boxShadow: 3,
                  background: 'rgba(168,134,133,0.95)',
                }}
              >
                ONLINE STORE
              </Button>
            </Box>

            {/* Fixed vertical stack for Facebook and Instagram on the left */}
            <Box sx={{ position: 'fixed', top: '10mm', left: '32px', zIndex: 1300, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                href="https://www.facebook.com/stylistinside/"
                target="_blank"
                sx={{
                  color: 'white',
                  borderRadius: '24px',
                  border: '2px solid white',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1.5,
                  minWidth: '48px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: 3,
                  background: 'rgba(168,134,133,0.95)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <FacebookIcon />
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                href="https://www.instagram.com/stylistinside/"
                target="_blank"
                sx={{
                  color: 'white',
                  borderRadius: '24px',
                  border: '2px solid white',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1.5,
                  minWidth: '48px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: 3,
                  background: 'rgba(168,134,133,0.95)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <InstagramIcon />
              </Button>
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
